import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  Typography,
  Button,
  IconButton,
  Chip,
  RadioGroup,
  Radio,
  FormControlLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Divider,
  Tooltip,
  CircularProgress,
  Alert,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format, parseISO } from "date-fns";
import { useTranslation } from "react-i18next";
import CommonModal from "../common/CommonModal";
import { ClassData } from "../../hooks/useClasses";
import { useSubjectsByClass } from "../../hooks/useSubjects";
import { useStudents } from "../../hooks/useStudents";
import { useError } from "../../contexts/ErrorContext";
import { useSessionClass } from "../../contexts/SessionClassContext";
import { validateFileSize, hasFormErrors, FieldValidation } from "../../utils/validation";
import {
  HomeworkAssignType,
  HomeworkAttachment,
  HomeworkRequestDto,
  HomeworkResponseDto,
  downloadHomeworkAttachment,
} from "../../hooks/useHomework";

const ALLOWED_EXTENSIONS = [".pdf", ".doc", ".docx", ".jpg", ".jpeg", ".png"];
const MAX_FILE_SIZE_MB = 10;
const TITLE_MAX_LENGTH = 100;
const DESCRIPTION_MAX_LENGTH = 500;

const formatClassLabel = (cls: ClassData) => `${cls.name} ${cls.section}`.trim();

const formatFileSize = (bytes: number): string => {
  if (!bytes) return "0 KB";
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
};

interface HomeworkFormModalProps {
  open: boolean;
  handleClose: () => void;
  onSubmit: (dto: HomeworkRequestDto, files: File[]) => void;
  isSubmitting?: boolean;
  isEdit?: boolean;
  initialData?: HomeworkResponseDto;
  schoolId?: number;
}

const HomeworkFormModal: React.FC<HomeworkFormModalProps> = ({
  open,
  handleClose,
  onSubmit,
  isSubmitting = false,
  isEdit = false,
  initialData,
  schoolId,
}) => {
  const { t } = useTranslation();
  const { showError } = useError();
  const { selectedSession, selectedClass } = useSessionClass();

  const [classId, setClassId] = useState<number | "">("");
  const [sessionId, setSessionId] = useState<number | "">("");
  const [subjectId, setSubjectId] = useState<number | "">("");
  const [assignmentDate, setAssignmentDate] = useState<Date | null>(new Date());
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState<Date | null>(null);
  const [assignType, setAssignType] = useState<HomeworkAssignType>("ALL");
  const [selectedStudentIds, setSelectedStudentIds] = useState<number[]>([]);
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [existingAttachments, setExistingAttachments] = useState<HomeworkAttachment[]>([]);
  // Ids of existing attachments removed in this session — sent to the backend on submit
  // (via handleFormSubmit) so the update API can delete them in the same request.
  const [removedAttachmentIds, setRemovedAttachmentIds] = useState<number[]>([]);
  const [errors, setErrors] = useState<FieldValidation>({});

  // Subjects are stored against a class NAME (e.g. "9th"), not a specific section's row id —
  // querying by classId only returns subjects for whichever section they happened to be
  // created under, so we look them up by base class name (section-agnostic) instead.
  const baseClassName = isEdit
    ? (initialData?.className || "").split(" ")[0]
    : selectedClass?.name || "";
  const subjectsQuery = useSubjectsByClass(baseClassName);
  // Students must be scoped to the session too — a class-row persists across sessions,
  // so a plain classId-only lookup would mix in students from other academic sessions.
  const { useStudentsBySessionIdAndClassId } = useStudents(false, schoolId);
  const studentsQuery = useStudentsBySessionIdAndClassId(sessionId || undefined, classId || undefined);

  useEffect(() => {
    if (!open) return;
    if (isEdit && initialData) {
      setClassId(initialData.classId);
      setSessionId(initialData.sessionId ?? "");
      setSubjectId(initialData.subjectId);
      setAssignmentDate(initialData.assignmentDate ? parseISO(initialData.assignmentDate) : null);
      setTitle(initialData.title || "");
      setDescription(initialData.description || "");
      setDueDate(initialData.dueDate ? parseISO(initialData.dueDate) : null);
      setAssignType(initialData.assignType || "ALL");
      setSelectedStudentIds(initialData.studentIds || []);
      setExistingAttachments(initialData.attachments || []);
    } else {
      setClassId(selectedClass?.id ?? "");
      setSessionId(selectedSession?.id ?? "");
      setSubjectId("");
      setAssignmentDate(new Date());
      setTitle("");
      setDescription("");
      setDueDate(null);
      setAssignType("ALL");
      setSelectedStudentIds([]);
      setExistingAttachments([]);
    }
    setNewFiles([]);
    setRemovedAttachmentIds([]);
    setErrors({});
  }, [open, isEdit, initialData, selectedClass?.id, selectedSession?.id]);

  // Class/Session are no longer user-editable — in edit mode we show the homework's own
  // original class/session, in create mode we mirror whatever is selected in the header.
  const resolvedClassName = isEdit
    ? initialData?.className
    : selectedClass
    ? formatClassLabel(selectedClass)
    : "";
  const resolvedSessionName = isEdit ? initialData?.sessionName : selectedSession?.name;
  const missingHeaderContext = !isEdit && (!selectedClass?.id || !selectedSession?.id);

  const selectedSubject = useMemo(
    () => subjectsQuery.data?.find((s) => s.id === subjectId),
    [subjectsQuery.data, subjectId]
  );

  const toggleStudent = (studentId: number) => {
    setSelectedStudentIds((prev) =>
      prev.includes(studentId) ? prev.filter((id) => id !== studentId) : [...prev, studentId]
    );
    setErrors((prev) => ({ ...prev, studentIds: null }));
  };

  const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files || []);
    const valid: File[] = [];
    selected.forEach((file) => {
      const ext = file.name.toLowerCase().slice(file.name.lastIndexOf("."));
      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        showError(
          `${file.name}: ${t("invalid_file_type") || "Invalid file type"} (allowed: pdf, doc, docx, jpg, jpeg, png)`
        );
        return;
      }
      if (!validateFileSize(file, MAX_FILE_SIZE_MB)) {
        showError(`${file.name}: ${t("file_too_large") || "File exceeds 10MB limit"}`);
        return;
      }
      valid.push(file);
    });
    if (valid.length) setNewFiles((prev) => [...prev, ...valid]);
    e.target.value = "";
  };

  const removeNewFile = (index: number) => {
    setNewFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRemoveExistingAttachment = (attachmentId: number) => {
    // Purely local — the actual deletion happens server-side when the update is submitted,
    // via removeAttachmentIds in the dto (see handleFormSubmit).
    setExistingAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
    setRemovedAttachmentIds((prev) => [...prev, attachmentId]);
  };

  const validate = (): FieldValidation => {
    const errs: FieldValidation = {};
    if (!subjectId) errs.subjectId = t("subject_is_required") || "Subject is required";
    if (!assignmentDate) errs.assignmentDate = t("date_of_assignment_is_required") || "Date of assignment is required";
    if (!title.trim()) {
      errs.title = t("title_is_required") || "Title is required";
    } else if (title.trim().length > TITLE_MAX_LENGTH) {
      errs.title = t("title_too_long") || `Title must be at most ${TITLE_MAX_LENGTH} characters`;
    }
    if (!description.trim()) {
      errs.description = t("description_is_required") || "Description is required";
    } else if (description.trim().length > DESCRIPTION_MAX_LENGTH) {
      errs.description = t("description_too_long") || `Description must be at most ${DESCRIPTION_MAX_LENGTH} characters`;
    }
    if (!dueDate) {
      errs.dueDate = t("due_date_is_required") || "Due date is required";
    } else if (assignmentDate && dueDate < assignmentDate) {
      errs.dueDate = t("due_date_before_assignment_date") || "Due date must be on/after assignment date";
    }
    if (assignType === "SELECTED" && selectedStudentIds.length === 0) {
      errs.studentIds = t("select_at_least_one_student") || "Select at least one student";
    }
    return errs;
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validate();
    setErrors(validationErrors);
    if (hasFormErrors(validationErrors)) {
      return;
    }

    const dto: HomeworkRequestDto = {
      schoolId: schoolId as number,
      classId: Number(classId),
      className: resolvedClassName,
      subjectId: Number(subjectId),
      subjectName: selectedSubject?.name || initialData?.subjectName,
      sessionId: Number(sessionId),
      sessionName: resolvedSessionName,
      assignmentDate: format(assignmentDate as Date, "yyyy-MM-dd"),
      title: title.trim(),
      description: description.trim(),
      dueDate: format(dueDate as Date, "yyyy-MM-dd"),
      assignType,
      studentIds: assignType === "SELECTED" ? selectedStudentIds : undefined,
      removeAttachmentIds: removedAttachmentIds,
    };

    onSubmit(dto, newFiles);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <CommonModal
        open={open}
        onClose={handleClose}
        title={isEdit ? t("edit_homework") || "Edit Homework" : t("assign_homework") || "Assign Homework"}
        maxWidth="md"
        fullWidth
        disableEscapeKeyDown
        onSubmit={handleFormSubmit}
        customActions={
          <>
            <Button
              type="button"
              variant="outlined"
              onClick={handleClose}
              sx={{
                minWidth: 100,
                borderColor: "#3092e3",
                color: "#3092e3",
                "&:hover": { borderColor: "#1a6fb5", color: "#1a6fb5", backgroundColor: "rgba(48,146,227,0.08)" },
              }}
            >
              {t("cancel") || "Cancel"}
            </Button>
            {/* Save as Draft — not supported by backend yet, hidden for now.
            <Tooltip title={t("save_as_draft_coming_soon") || "Coming soon — not yet supported"}>
              <span>
                <Button type="button" variant="outlined" disabled sx={{ minWidth: 100, ml: 1 }}>
                  {t("save_as_draft") || "Save as Draft"}
                </Button>
              </span>
            </Tooltip>
            */}
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting || missingHeaderContext}
              sx={{
                minWidth: 140,
                ml: 1,
                backgroundColor: "#3092e3",
                "&:hover": { backgroundColor: "primary.main" },
                "&.Mui-disabled": { backgroundColor: "#b2ebff", opacity: 0.6 },
              }}
            >
              {isSubmitting ? (
                <CircularProgress size={20} sx={{ color: "#fff" }} />
              ) : isEdit ? (
                t("edit_homework") || "Update Homework"
              ) : (
                t("assign_homework") || "Assign Homework"
              )}
            </Button>
          </>
        }
      >
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {missingHeaderContext && (
            <Grid item xs={12}>
              <Alert severity="warning">
                {t("please_select_session_and_class") || "Please select a session and class"}
              </Alert>
            </Grid>
          )}

          <Grid item xs={12} sm={6}>
            <TextField
              label={t("session") || "Session"}
              value={resolvedSessionName || ""}
              fullWidth
              size="small"
              InputProps={{ readOnly: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label={t("class") || "Class"}
              value={resolvedClassName || ""}
              fullWidth
              size="small"
              InputProps={{ readOnly: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small" error={!!errors.subjectId} disabled={!classId}>
              <InputLabel>{t("subject") || "Subject"}</InputLabel>
              <Select
                label={t("subject") || "Subject"}
                value={subjectId}
                onChange={(e) => {
                  const value = e.target.value === "" ? "" : Number(e.target.value);
                  setSubjectId(value);
                  setErrors((prev) => ({ ...prev, subjectId: null }));
                }}
              >
                {(subjectsQuery.data ?? []).map((subject) => (
                  <MenuItem key={subject.id} value={subject.id}>
                    {subject.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.subjectId && <FormHelperText>{errors.subjectId}</FormHelperText>}
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <DatePicker
              label={t("date_of_assignment") || "Date of Assignment"}
              value={assignmentDate}
              onChange={(date) => {
                setAssignmentDate(date);
                setErrors((prev) => ({ ...prev, assignmentDate: null }));
              }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: "small",
                  error: !!errors.assignmentDate,
                  helperText: errors.assignmentDate || "",
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <DatePicker
              label={t("due_date") || "Due Date"}
              value={dueDate}
              minDate={assignmentDate ?? undefined}
              onChange={(date) => {
                setDueDate(date);
                setErrors((prev) => ({ ...prev, dueDate: null }));
              }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: "small",
                  error: !!errors.dueDate,
                  helperText: errors.dueDate || "",
                },
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label={t("title") || "Title"}
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                setErrors((prev) => ({ ...prev, title: null }));
              }}
              fullWidth
              size="small"
              required
              error={!!errors.title}
              helperText={errors.title || `${title.length}/${TITLE_MAX_LENGTH}`}
              inputProps={{ maxLength: TITLE_MAX_LENGTH }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label={t("description_instructions") || "Description / Instructions"}
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                setErrors((prev) => ({ ...prev, description: null }));
              }}
              fullWidth
              multiline
              rows={4}
              required
              error={!!errors.description}
              helperText={errors.description || `${description.length}/${DESCRIPTION_MAX_LENGTH}`}
              inputProps={{ maxLength: DESCRIPTION_MAX_LENGTH }}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t("attachments") || "Attachments"}
            </Typography>
            <Button
              component="label"
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              sx={{ borderColor: "#3092e3", color: "#3092e3" }}
            >
              {t("upload_files") || "Upload Files"}
              <input
                type="file"
                multiple
                hidden
                accept={ALLOWED_EXTENSIONS.join(",")}
                onChange={handleFilesSelected}
              />
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
              {t("allowed_file_types_hint") || "Allowed: pdf, doc, docx, jpg, jpeg, png (max 10MB each)"}
            </Typography>

            {existingAttachments.length > 0 && (
              <List dense sx={{ mt: 1 }}>
                {existingAttachments.map((att) => (
                  <ListItem
                    key={att.id}
                    secondaryAction={
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <IconButton
                          size="small"
                          onClick={() => downloadHomeworkAttachment(att.fileName, att.originalFileName)}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleRemoveExistingAttachment(att.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    }
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <InsertDriveFileIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={att.originalFileName}
                      secondary={formatFileSize(att.fileSize)}
                    />
                  </ListItem>
                ))}
              </List>
            )}

            {newFiles.length > 0 && (
              <List dense sx={{ mt: 1 }}>
                {newFiles.map((file, index) => (
                  <ListItem
                    key={`${file.name}-${index}`}
                    secondaryAction={
                      <IconButton size="small" onClick={() => removeNewFile(index)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    }
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <InsertDriveFileIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={file.name} secondary={formatFileSize(file.size)} />
                  </ListItem>
                ))}
              </List>
            )}
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t("assign_to") || "Assign To"}
            </Typography>
            <RadioGroup
              row
              value={assignType}
              onChange={(e) => {
                setAssignType(e.target.value as HomeworkAssignType);
                setErrors((prev) => ({ ...prev, studentIds: null }));
              }}
            >
              <FormControlLabel value="ALL" control={<Radio />} label={t("all_students") || "All Students"} />
              <FormControlLabel
                value="SELECTED"
                control={<Radio />}
                label={t("select_students") || "Select Students"}
              />
            </RadioGroup>

            {assignType === "SELECTED" && (
              <Box sx={{ mt: 1 }}>
                {!classId ? (
                  <Typography variant="body2" color="text.secondary">
                    {t("select_class_to_see_students") || "Select a class first to see students."}
                  </Typography>
                ) : studentsQuery.isLoading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <List
                    dense
                    sx={{ border: "1px solid #e0e0e0", borderRadius: 1, maxHeight: 220, overflow: "auto" }}
                  >
                    {(studentsQuery.data ?? []).length === 0 ? (
                      <ListItem>
                        <ListItemText primary={t("no_students_found_for_this_class") || "No students found for this class."} />
                      </ListItem>
                    ) : (
                      (studentsQuery.data ?? []).map((student) => {
                        const checked = selectedStudentIds.includes(student.id);
                        return (
                          <ListItem key={student.id} disablePadding>
                            <ListItemButton onClick={() => toggleStudent(student.id)}>
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <Checkbox edge="start" checked={checked} tabIndex={-1} disableRipple />
                              </ListItemIcon>
                              <ListItemText
                                primary={student.studentName}
                                secondary={student.rollNumber ? `Roll No. ${student.rollNumber}` : undefined}
                              />
                            </ListItemButton>
                          </ListItem>
                        );
                      })
                    )}
                  </List>
                )}
                {errors.studentIds && (
                  <FormHelperText error sx={{ mt: 0.5 }}>
                    {errors.studentIds}
                  </FormHelperText>
                )}
                {selectedStudentIds.length > 0 && (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                    {(studentsQuery.data ?? [])
                      .filter((s) => selectedStudentIds.includes(s.id))
                      .map((student) => (
                        <Chip
                          key={student.id}
                          label={student.studentName}
                          onDelete={() => toggleStudent(student.id)}
                        />
                      ))}
                  </Box>
                )}
              </Box>
            )}
          </Grid>
        </Grid>
      </CommonModal>
    </LocalizationProvider>
  );
};

export default HomeworkFormModal;
